/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import { Injectable, OnDestroy } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, Subject } from 'rxjs';
import {
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    IApplicationLog,
    IConnectionLog,
    IWafRuleLog,
} from 'generated-types';

import { VsLogsStore } from '../../services/vs-logs.store';
import { VsLogListStore } from '../vs-log-list/vs-log-list.store';
import { TVsLog, VsLogsType } from '../../vs-logs.types';
import {
    IL4ConnectionInfo,
    TVsLogApplicationInfo,
    TVsLogHttpRequestHeaders,
    TVsLogHttpResponseHeaders,
    TWafRuleLogsByGroup,
} from './vs-log-cinematic.types';

import { isObjectEmpty } from '../../utils/vs-logs.utils';

import {
    DnsClientInfoPipe,
    DnsLoadBalancerInfoPipe,
    L4ClientInfoPipe,
    L7ClientInfoPipe,
    NsxAlbInfoPipe,
    PolicyRulesPipe,
    RequestDetailsPipe,
    RequestInfoPipe,
    ResponseInfoPipe,
} from '../../pipes/summary-content-info';

/**
 * State type for the store.
 * There can be either applicationLog or connectionLog set as they are mutually exlusive.
 * @param applicationLog - Exists only when the VS is of type L7.
 * @param connectionLog - Exists only when the VS is of type L4 or DNS.
 * @param hasPreviousLog - True if there's previous log entry to switch to.
 * @param hasNextLog - True if there's a next log entry to switch to.
 */
interface IStateTypes {
    applicationLog: IApplicationLog;
    connectionLog: IConnectionLog;
    hasPreviousLog: boolean;
    hasNextLog: boolean;
}

const initialState: IStateTypes = {
    applicationLog: {},
    connectionLog: {},
    hasPreviousLog: false,
    hasNextLog: false,
};

/**
 * @description
 *     State and Effect management for VsLogCinematic and its children components.
 * @author Zhiqian Liu, Alex Klyuev
 */
@Injectable()
export class VsLogCinematicStore extends ComponentStore<IStateTypes> implements OnDestroy {
    public readonly applicationLog$ = this.select(state => state.applicationLog);

    public readonly connectionLog$ = this.select(state => state.connectionLog);

    /**
     * Select the log loaded.
     * If nothing loaded, an empty object is guaranteed to be returned.
     */
    public readonly vsLog$ = this.select<TVsLog>(
        ({ applicationLog, connectionLog }) => {
            return !isObjectEmpty(applicationLog) ? applicationLog : connectionLog;
        },
    );

    public readonly hasPreviousLog$ = this.select<boolean>(({ hasPreviousLog }) => hasPreviousLog);
    public readonly hasNextLog$ = this.select<boolean>(({ hasNextLog }) => hasNextLog);

    /**
     * Indicate whether a Log has Avi defined filters.
     */
    public readonly isSignificantLog$ = this.select(this.vsLog$, ({ adf }) => adf);

    // ******************************** Generic Log Selectors ************************************

    public readonly timestamp$ = this.select(
        this.vsLog$,
        ({ report_timestamp }) => report_timestamp,
    );
    public readonly clientRtt$ = this.select(this.vsLog$, ({ client_rtt }) => client_rtt);

    public readonly serverRtt$ = this.select(this.vsLog$, ({ server_rtt }) => server_rtt);

    public readonly totalTime$ = this.select(this.vsLog$, ({ total_time }) => (
        total_time ? Number(total_time) : total_time
    ));

    public readonly pool$ = this.select(this.vsLog$, ({ pool }) => pool);

    public readonly poolName$ = this.select(this.vsLog$, ({ pool_name }) => pool_name);

    public readonly clientIps$ = this.select(
        this.vsLog$, ({ client_ip6, client_ip }) => {
            return {
                client_ip,
                client_ip6,
            };
        },
    );

    public readonly clientSrcPort$ = this.select(
        this.vsLog$,
        ({ client_src_port }) => client_src_port,
    );

    public readonly clientDestPort$ = this.select(
        this.vsLog$,
        ({ client_dest_port }) => client_dest_port,
    );

    public readonly vsIps$ = this.select(
        this.vsLog$, ({ vs_ip, vs_ip6 }) => {
            return {
                vs_ip,
                vs_ip6,
            };
        },
    );

    public readonly serverName$ = this.select(
        this.vsLog$,
        ({ server_name }) => server_name,
    );

    public readonly serverIps$ = this.select(
        this.vsLog$, ({ server_ip, server_ip6 }) => {
            return {
                server_ip,
                server_ip6,
            };
        },
    );

    public readonly serverSrcPort$ = this.select(
        this.vsLog$, ({ server_src_port }) => server_src_port,
    );

    public readonly serverDestPort$ = this.select(
        this.vsLog$, ({ server_dest_port }) => server_dest_port,
    );

    public readonly serverConnectionSrcIps$ = this.select(
        this.vsLog$,
        ({ server_conn_src_ip, server_conn_src_ip6 }) => {
            return {
                server_conn_src_ip,
                server_conn_src_ip6,
            };
        },
    );

    public readonly serviceEngine$ = this.select(
        this.vsLog$, ({ service_engine }) => service_engine,
    );

    public readonly vcpuId$ = this.select(this.vsLog$, ({ vcpu_id }) => vcpu_id);

    public readonly significance$ = this.select(this.vsLog$, ({ significance }) => significance);

    public readonly significantLog$ = this.select(
        this.vsLog$, ({ significant_log: significanceLog }) => significanceLog,
    );

    public readonly uriPath$ = this.select(this.applicationLog$, ({ uri_path }) => uri_path);

    // ****************************** Application Log Selectors **********************************

    public readonly responseCode$ = this.select(
        ({ applicationLog: { response_code } }) => response_code,
    );

    public readonly serverResponseCode$ = this.select(
        ({ applicationLog: { server_response_code } }) => server_response_code,
    );

    public readonly appResponseTime$ = this.select(
        ({ applicationLog: { app_response_time } }) => (
            app_response_time ? Number(app_response_time) : app_response_time
        ),
    );

    public readonly dataTransferTime$ = this.select(
        ({ applicationLog: { data_transfer_time } }) => (
            data_transfer_time ? Number(data_transfer_time) : data_transfer_time
        ),
    );

    // TODO: need to confirm what comes as source IP port
    public readonly sourceIps$ = this.select(
        ({ applicationLog: { source_ip, source_ip6 } }) => {
            return {
                source_ip,
                source_ip6,
            };
        },
    );

    public readonly l7ClientInfo$ = this.select(
        ({ applicationLog }) => this.l7ClientInfoPipe.transform(applicationLog),
    );

    public readonly ssl$ = this.select(
        ({ applicationLog }) => {
            const {
                ssl_version: sslVersion,
                sni_hostname: sniHostname,
                ocsp_status_resp_sent: ocspStatusRespSent,
                ssl_cipher: sslCipher,
                client_fingerprints: clientFingerprints,
                client_cipher_list: clientCipherList,
                ssl_signature_algorithm: sslSignatureAlgorithm,
            } = applicationLog;

            const appLog = applicationLog as any;
            // const sslSignatureAlgorithm = appLog.ssl_signature_algorithm;
            const sslNamedGroup = appLog.ssl_named_group;

            const ssl = {
                ssl_version: sslVersion,
                sni_hostname: sniHostname,
                ocsp_status_resp_sent: ocspStatusRespSent,
                ssl_cipher: sslCipher,
                client_fingerprints: clientFingerprints,
                client_cipher_list: clientCipherList,
                ssl_signature_algorithm: sslSignatureAlgorithm,
                ssl_named_group: sslNamedGroup,
            };

            if (isObjectEmpty(ssl)) {
                return null;
            }

            return ssl;
        },
    );

    public readonly nsxAlbInfo$ = this.select(
        ({ applicationLog }) => this.nsxAlbInfoPipe.transform(applicationLog),
    );

    public readonly policyRules$ = this.select(
        ({ applicationLog }) => this.policyRulesPipe.transform(applicationLog),
    );

    public readonly requestInfo$ = this.select(
        ({ applicationLog }) => this.requestInfoPipe.transform(applicationLog),
    );

    public readonly requestDetails$ = this.select(
        ({ applicationLog }) => this.requestDetailsPipe.transform(applicationLog),
    );

    public readonly responseInfo$ = this.select(
        ({ applicationLog }) => this.responseInfoPipe.transform(applicationLog),
    );

    public readonly httpRequestHeaders$ = this.select<TVsLogHttpRequestHeaders>(
        ({ applicationLog: {
            all_request_headers,
            headers_sent_to_server,
        } }) => {
            const httpResponseHeaders = {
                all_request_headers,
                headers_sent_to_server,
            };

            if (isObjectEmpty(httpResponseHeaders)) {
                return null;
            }

            return httpResponseHeaders;
        },
    );

    public readonly httpResponseHeaders$ = this.select<TVsLogHttpResponseHeaders>(
        ({ applicationLog: {
            all_response_headers,
            headers_received_from_server,
        } }) => {
            const httpResponseHeaders = {
                all_response_headers,
                headers_received_from_server,
            };

            if (isObjectEmpty(httpResponseHeaders)) {
                return null;
            }

            return httpResponseHeaders;
        },
    );

    public readonly applicationInfo$ = this.select<TVsLogApplicationInfo>(
        ({ applicationLog: {
            http2_stream_id,
            server_push_initiated,
            server_pushed_request,
            cache_hit,
            compression,
            compression_percentage,
            servers_tried,
            method,
        } }) => {
            const applicationInfo = {
                http2_stream_id,
                server_push_initiated,
                server_pushed_request,
                cache_hit,
                compression,
                compression_percentage,
                servers_tried,
                method,
            };

            if (isObjectEmpty(applicationInfo)) {
                return null;
            }

            return applicationInfo;
        },
    );

    public readonly grpcInfo$ = this.select(
        ({ applicationLog: {
            grpc_service_name,
            grpc_method_name,
            grpc_status_reason_phrase,
        } }) => {
            const grpcInfo = {
                grpc_service_name,
                grpc_method_name,
                grpc_status_reason_phrase,
            };

            if (isObjectEmpty(grpcInfo)) {
                return null;
            }

            return grpcInfo;
        },
    );

    public readonly datascriptInfo$ = this.select(
        ({
            applicationLog: {
                datascript_error_trace,
                datascript_log,
            },
            connectionLog: {
                ds_log,
            },
        }) => {
            const datascriptInfo = {
                datascript_error_trace,
                datascript_log,
                ds_log,
            };

            if (isObjectEmpty(datascriptInfo)) {
                return null;
            }

            return datascriptInfo;
        },
    );

    public readonly wafAllowListLogs$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }) => wafLog?.allowlist_logs,
    );

    public readonly wafLog$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }) => wafLog,
    );

    public readonly wafPsmLogs$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }) => wafLog?.psm_logs,
    );

    public readonly wafStatus$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }) => wafLog?.status,
    );

    public readonly wafUsedBodyParser$ = this.select(
        this.wafLog$,
        ({ used_body_parser }) => used_body_parser,
    );

    /** Datascript request logs under out of band log */
    public readonly oobDsRequestLogs$ = this.select(
        ({ applicationLog: { oob_log } }) => oob_log?.ds_req_logs,
    );

    public readonly samlLog$ = this.select(
        ({ applicationLog: { saml_log } }) => saml_log,
    );

    public readonly oAuthLog$ = this.select(
        ({ applicationLog: { oauth_log } }) => oauth_log,
    );

    public readonly icapLog$ = this.select(
        ({ applicationLog: { icap_log } }) => icap_log,
    );

    public readonly jwtLog$ = this.select(
        ({ applicationLog: { jwt_log } }) => jwt_log,
    );

    public readonly ntlmLog$ = this.select(
        ({ applicationLog: { ntlm_log } }) => ntlm_log,
    );

    // return true if one of the authentication logs is present
    public readonly hasAuthenticationLogs$ = this.select(
        this.samlLog$,
        this.oAuthLog$,
        this.jwtLog$,
        this.ntlmLog$,
        (samlLog, oAuthLog, jwtLog, ntlmLog) => {
            return Boolean(samlLog || oAuthLog || jwtLog || ntlmLog);
        },
    );

    public readonly wafApplicationRuleLogs$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }): IWafRuleLog[] => wafLog?.application_rule_logs,
    );

    public readonly wafRuleLogs$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }): IWafRuleLog[] => wafLog?.rule_logs,
    );

    public readonly wafRuleLogsByGroup$ = this.select(
        this.wafRuleLogs$,
        (ruleLogs: IWafRuleLog[] = []): TWafRuleLogsByGroup => {
            return ruleLogs.reduce((output, ruleLog, index) => {
                const { rule_group: ruleGroup = `__N/A:${index}` } = ruleLog;

                output[ruleGroup] = output[ruleGroup] || [];
                output[ruleGroup].push(ruleLog);

                return output;
            }, {} as any as TWafRuleLogsByGroup);
        },
    );

    public readonly botManagementLog$ = this.select(
        ({ applicationLog: { bot_management_log: botManagementLog } }) => botManagementLog,
    );

    // ****************************** Connection Log Selectors **********************************
    public readonly dnsClientInfo$ = this.select(
        ({ connectionLog }) => this.dnsClientInfoPipe.transform(connectionLog),
    );

    public readonly dnsLoadBalancerInfo$ = this.select(
        ({ connectionLog }) => this.dnsLoadBalancerInfoPipe.transform(connectionLog),
    );

    public readonly dnsRequestInfo$ = this.select(
        ({ connectionLog: { dns_request } }) => dns_request,
    );

    public readonly dnsResponseInfo$ = this.select(
        ({ connectionLog: { dns_response } }) => dns_response,
    );

    // ************************************ L4 Log Selectors **************************************

    public readonly l4ClientConnectionInfo$ = this.select<IL4ConnectionInfo>(
        ({ connectionLog: {
            tx_bytes: transmittedBytes,
            tx_pkts: transmittedPackets,
            rx_bytes: receivedBytes,
            rx_pkts: receivedPackets,
            total_bytes: totalBytes,
            total_pkts: totalPackets,
            out_of_orders: outOfOrders,
            timeouts,
            retransmits,
        } }) => {
            const l4ClientConnectionInfo = {
                transmittedBytes,
                transmittedPackets,
                receivedBytes,
                receivedPackets,
                totalBytes,
                totalPackets,
                outOfOrders,
                timeouts,
                retransmits,
            };

            if (isObjectEmpty(l4ClientConnectionInfo)) {
                return null;
            }

            return l4ClientConnectionInfo;
        },
    );

    public readonly l4ServerConnectionInfo$ = this.select<IL4ConnectionInfo>(
        ({ connectionLog: {
            server_tx_bytes: transmittedBytes,
            server_tx_pkts: transmittedPackets,
            server_rx_bytes: receivedBytes,
            server_rx_pkts: receivedPackets,
            server_total_bytes: totalBytes,
            server_total_pkts: totalPackets,
            server_out_of_orders: outOfOrders,
            server_timeouts: timeouts,
            server_retransmits: retransmits,
        } }) => {
            const l4ServerConnectionInfo = {
                transmittedBytes,
                transmittedPackets,
                receivedBytes,
                receivedPackets,
                totalBytes,
                totalPackets,
                outOfOrders,
                timeouts,
                retransmits,
            };

            if (isObjectEmpty(l4ServerConnectionInfo)) {
                return null;
            }

            return l4ServerConnectionInfo;
        },
    );

    public readonly l4ClientInfo$ = this.select(
        ({ connectionLog }) => this.l4ClientInfoPipe.transform(connectionLog),
    );

    // *************************************** Effects ****************************************
    /**
     * Subject to close the cinematic view.
     */
    public readonly closeCinematic$ = new Subject<void>();

    // *************************************** Updaters ****************************************

    public readonly setErrors = this.updater((state, errors: string | object) => ({
        ...state,
        errors,
    }));

    public readonly setErrorFlag = this.updater((state, hasError: boolean) => ({
        ...state,
        hasError,
    }));

    /**
     * Update state with the newly switched log, depending on the type of log.
     */
    public readonly setLogBasedOnType = this.effect<TVsLog>(
        log$ => log$.pipe(
            withLatestFrom(this.vsLogsStore.vsLogsType$),
            tap(([log, vsLogsType]: [TVsLog, VsLogsType]) => {
                switch (log && vsLogsType) {
                    case VsLogsType.APPLICATION:
                        this.setApplicationLog(log as IApplicationLog);
                        break;

                    case VsLogsType.CONNECTION:
                        this.setConnectionLog(log as IConnectionLog);
                        break;
                }
            }),
        ),
    );

    private readonly setApplicationLog = this.updater((state, applicationLog: IApplicationLog) => ({
        ...state,
        applicationLog,
    }));

    private readonly setConnectionLog = this.updater((state, connectionLog: IApplicationLog) => ({
        ...state,
        connectionLog,
    }));

    private readonly setHasPreviousLog = this.updater((state, hasPreviousLog: boolean) => ({
        ...state,
        hasPreviousLog,
    }));

    private readonly setHasNextLog = this.updater((state, hasNextLog: boolean) => ({
        ...state,
        hasNextLog,
    }));

    private storeDestroySubject$ = new Subject<void>();

    // ************************************* Constructor *************************************

    constructor(
        private readonly vsLogsStore: VsLogsStore,
        private readonly dnsClientInfoPipe: DnsClientInfoPipe,
        private readonly dnsLoadBalancerInfoPipe: DnsLoadBalancerInfoPipe,
        private readonly l4ClientInfoPipe: L4ClientInfoPipe,
        private readonly l7ClientInfoPipe: L7ClientInfoPipe,
        private readonly policyRulesPipe: PolicyRulesPipe,
        private readonly nsxAlbInfoPipe: NsxAlbInfoPipe,
        private readonly requestInfoPipe: RequestInfoPipe,
        private readonly requestDetailsPipe: RequestDetailsPipe,
        private readonly responseInfoPipe: ResponseInfoPipe,
        vsLogListStore: VsLogListStore,
    ) {
        super(initialState);

        vsLogListStore.recentlyViewedLog$
            .pipe(takeUntil(this.storeDestroySubject$))
            .subscribe(log => {
                this.setLogBasedOnType(log);
            });

        combineLatest([
            vsLogListStore.recentlyViewedLogIndex$,
            vsLogsStore.vsLogListPaginationInfo$,
        ])
            .pipe(takeUntil(this.storeDestroySubject$))
            .subscribe(
                ([currentIndex, { page, pageSize, listLogCount }]) => {
                    // page starts from 1, currentIndex starts from 0
                    const overallPosition = currentIndex + 1 + (page - 1) * pageSize;

                    this.setHasPreviousLog(overallPosition > 1);
                    this.setHasNextLog(overallPosition < listLogCount);
                },
            );
    }

    /** @override */
    public ngOnDestroy(): void {
        this.storeDestroySubject$.next();
        this.storeDestroySubject$.complete();
        this.closeCinematic$.complete();
    }

    // ************************************* Methods *************************************

    /**
     * Close the cinematic view.
     */
    public closeCinematic(): void {
        this.closeCinematic$.next();
    }
}
